import { useState, useMemo, useCallback, useEffect } from "react";
import { HardHat, ShieldAlert, CheckCircle2, Circle, Loader2, Send, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import Disclaimer from "@/components/Disclaimer";
import BiSelect from "@/components/BiSelect";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { useApp } from "@/contexts/AppContext";
import { formatDateTime } from "@/shared/format";

/**
 * OHS / Take 5 — pre-start safety attestations bound to the active Job.
 *
 * Each submitted checklist writes ONE `take5` docket to the backend so the
 * accountant / builder can audit-trail compliance per job. The Jobs page
 * surfaces the count + latest-at in a Safety chip and expands to a full log.
 *
 * UX details in play here:
 *  - "Securing your data…" progress bar while POSTing (files are small but
 *    on-site 4G is unreliable; showing a bar prevents double-taps).
 *  - Green "Safety Check Logged" toast + local docket count bump on success.
 *  - Job dropdown defaults to the AppContext `activeJobId` (persisted to
 *    localStorage) so tradies don't re-pick every morning.
 */
const CHECKLISTS = [
  {
    title: "Daily site pre-start",
    items: [
      "First-aid kit onsite & stocked",
      "SWMS reviewed for today's tasks",
      "PPE (boots, hi-vis, hard hat) worn",
      "Fire extinguisher accessible",
      "Emergency exits clear",
      "Traffic control if required",
    ],
  },
  {
    title: "Working at heights (>2m)",
    items: [
      "Guardrails or edge protection",
      "Harnesses inspected",
      "Ladders on stable base, secured",
      "Fall zone clear below",
      "Trained/competent operators only",
    ],
  },
  {
    title: "Electrical & hot works",
    items: [
      "Test tag valid on all tools",
      "RCDs in use",
      "Hot work permit signed off",
      "Fire watch present 30 min after",
      "No flammables within 10m",
    ],
  },
];

export default function OHS() {
  const { jobs, activeJobId, setActiveJobId } = useApp();
  const [state, setState] = useState({});
  const [notes, setNotes] = useState("");
  const [activeChecklist, setActiveChecklist] = useState(CHECKLISTS[0].title);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0); // 0..100 for the "Securing…" bar
  const [recent, setRecent] = useState([]);

  const toggle = useCallback((k) => setState((s) => ({ ...s, [k]: !s[k] })), []);

  const activeConfig = useMemo(
    () => CHECKLISTS.find((c) => c.title === activeChecklist) || CHECKLISTS[0],
    [activeChecklist]
  );

  const loadRecent = useCallback(async () => {
    if (!activeJobId) { setRecent([]); return; }
    try {
      const { data } = await api.get(`/take5?job_id=${encodeURIComponent(activeJobId)}`);
      setRecent(Array.isArray(data) ? data.slice(0, 5) : []);
    } catch (err) {
      devLog.warn("take5 recent fetch failed", err);
    }
  }, [activeJobId]);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  const submit = useCallback(async () => {
    if (!activeJobId) {
      toast.error("Pick a job first — every safety check binds to a job.");
      return;
    }
    setSaving(true);
    setProgress(8);
    // Fake-progress the bar so the tradie sees motion even on fast networks —
    // real bandwidth here is < 5 KB so anything else looks frozen.
    const tick = setInterval(() => {
      setProgress((p) => (p >= 88 ? p : p + 6));
    }, 120);

    try {
      const items = activeConfig.items.map((label) => ({
        key: label,
        checked: !!state[`${activeConfig.title}::${label}`],
      }));
      await api.post("/take5", {
        job_id: activeJobId,
        title: activeConfig.title,
        items,
        notes: notes.trim() || null,
      });
      setProgress(100);
      toast.success("Safety Check Logged", {
        description: `${activeConfig.title} · saved to job.`,
      });
      setState({});
      setNotes("");
      loadRecent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save safety check");
    } finally {
      clearInterval(tick);
      // brief hold at 100% so the tradie sees "finished" before the bar goes away
      setTimeout(() => { setProgress(0); setSaving(false); }, 350);
    }
  }, [activeJobId, activeConfig, state, notes, loadRecent]);

  const doneCount = useMemo(
    () => activeConfig.items.filter((it) => state[`${activeConfig.title}::${it}`]).length,
    [activeConfig, state]
  );

  return (
    <div className="space-y-4" data-testid="ohs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold">OHS / Take 5</h1>
          <div className="text-sm text-white/60">Log a Take 5 safety check against the current job.</div>
        </div>
        <div className="w-10 h-10 rounded-full bg-[#FBBF24]/20 text-[#FBBF24] flex items-center justify-center"><HardHat className="w-5 h-5" /></div>
      </div>

      {/* Job picker — bound to the same activeJobId the Capture flow uses. */}
      <div className="bi-surface p-4 space-y-2">
        <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading">Job</label>
        {jobs.length === 0 ? (
          <div className="text-sm text-white/60">
            No jobs yet — head to <b>Jobs</b> to add one, then come back to log a Take 5.
          </div>
        ) : (
          <BiSelect
            data-testid="ohs-job-select"
            value={activeJobId || ""}
            onChange={(e) => setActiveJobId(e.target.value)}
            className="bi-input w-full"
          >
            <option value="">Select job…</option>
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>{j.name}{j.client ? ` — ${j.client}` : ""}</option>
            ))}
          </BiSelect>
        )}
      </div>

      <div className="bi-surface p-4 flex items-start gap-3">
        <ShieldAlert className="w-5 h-5 text-[#F87171] shrink-0 mt-0.5" />
        <div className="text-sm text-white/80">
          <b>Risk alert:</b> Working with silica dust? Wet-cut only and use P2 respirators. Silicosis is preventable.
        </div>
      </div>

      {/* Tabs for checklist choice — keeps the page short. */}
      <div className="flex gap-1.5 flex-wrap">
        {CHECKLISTS.map((c) => {
          const slug = c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
          return (
            <button
              key={c.title}
              data-testid={`ohs-tab-${slug}`}
              onClick={() => { setActiveChecklist(c.title); setState({}); }}
              className={`h-9 px-3 rounded-full text-[11px] font-heading font-bold uppercase tracking-wider transition ${
                activeChecklist === c.title
                  ? "bg-[#FF9500] text-black"
                  : "bg-white/[0.06] text-white/70 hover:bg-white/[0.1]"
              }`}
            >
              {c.title}
            </button>
          );
        })}
      </div>

      <div className="bi-surface p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="font-heading font-bold">{activeConfig.title}</div>
          <div className="text-[10px] text-white/50">{doneCount}/{activeConfig.items.length} done</div>
        </div>
        <div className="space-y-1">
          {activeConfig.items.map((it) => {
            const k = `${activeConfig.title}::${it}`;
            const done = !!state[k];
            return (
              <button
                key={k}
                data-testid={`ohs-item-${it.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => toggle(k)}
                className="w-full flex items-center gap-3 py-2.5 text-left transition"
              >
                {done ? <CheckCircle2 className="w-5 h-5 text-[#34D399] shrink-0" /> : <Circle className="w-5 h-5 text-white/30 shrink-0" />}
                <span className={`text-sm ${done ? "text-white/50 line-through" : "text-white/90"}`}>{it}</span>
              </button>
            );
          })}
        </div>

        <textarea
          data-testid="ohs-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes for the accountant / site log (optional)…"
          rows={2}
          className="bi-input w-full mt-3 text-sm"
          maxLength={2000}
        />

        {/* "Securing your data…" progress bar */}
        {saving && (
          <div className="mt-3 space-y-1" data-testid="ohs-progress">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-heading inline-flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Securing your data…
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#FF9500] to-[#FFB86C] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        <button
          type="button"
          data-testid="ohs-submit"
          onClick={submit}
          disabled={saving || !activeJobId}
          className="mt-3 h-11 w-full rounded-xl bi-accent-btn inline-flex items-center justify-center gap-2 text-sm font-heading font-bold uppercase tracking-wider disabled:opacity-50 active:scale-[0.98] transition"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {saving ? "Saving…" : "Log Safety Check"}
        </button>
      </div>

      {/* Recent Take 5 dockets for the active job */}
      {activeJobId && recent.length > 0 && (
        <div className="bi-surface p-4 space-y-2" data-testid="ohs-recent">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-white/50" />
            <div className="font-heading font-bold text-sm">Recent for this job</div>
          </div>
          {recent.map((r) => {
            const checked = (r.items || []).filter((i) => i.checked).length;
            const total = (r.items || []).length;
            const dt = r.created_at ? new Date(r.created_at) : null;
            return (
              <div
                key={r.id}
                data-testid={`ohs-recent-${r.id}`}
                className="flex items-center gap-3 text-[11px] py-2 border-b border-white/5 last:border-b-0"
              >
                <CheckCircle2 className="w-4 h-4 text-[#34D399] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-white/85 truncate">{r.title}</div>
                  <div className="text-white/40">
                    {checked}/{total} items · {dt ? formatDateTime(dt.toISOString()) : ""}
                    {r.notes ? " · Notes attached" : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Disclaimer text="Checklists are guidance only and not a substitute for a Safe Work Method Statement (SWMS) or WHS Act compliance. Follow your state regulations." />
    </div>
  );
}
